/**
 * MUI Devices component that replaces the styled-components Devices component
 * 
 * This component provides a Material UI equivalent of the original Devices component
 * with similar props and functionality.
 * 
 * @module MuiDeviceDrawing
 * @description Module for rendering various audio devices (speakers, microphones, horns) in a SVG context using Material UI.
 */
import React, { useMemo, useContext, useState, useRef, Fragment, useEffect, useLayoutEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector, useStore } from "react-redux";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { getConditionResult, getCurrentValue2 } from "reduxModules/selectors/containerSelectors";
import type { SpeakerPayloadMeta } from 'types/middleware/actionMeta';
import ReducersIndex from 'reduxModules/ducks';
import ToolContext from "components/services/ToolContext";
import icons from "globalConstants/Icons";
import { useIconThemes } from "globalConstants/Themes";
import useMousePosition from "hooks/useMousePosition";
import { useAlert } from "hooks/useAlert";
import { clamp, has, mapKeys } from "lodash";
import { micInCategory } from "types/models/microphone";
import { evaluateTileDrop } from "reduxModules/calculations/roomCalculations";
import { 
  MuiDeviceProps,
  MuiSpeakersProps,
  MuiMicrophonesProps,
  MuiHornsProps,
  MuiCamerasProps,
  DeviceGroupProps,
  DeviceCollisionProps,
  DeviceObject,
  DeviceLayoutItem,
  DeviceCollection
} from "types/components/elements";
import { RootState } from "types/redux/state";

// Width (in glyph units) of the rounded-square body of the `ceiling_tile_mic` icon
// (<rect width="47"> in Icons/Devices.js). Used to scale that icon so its body
// matches the BMA360D's physical tile footprint on the canvas.
const CEILING_TILE_ICON_BODY_UNITS = 47;
// Fraction of the tile-scaled icon at which the inner mic glyph is drawn, so it stays
// a balanced size inside the (larger) tile square rather than filling it. Tune to taste.
const CEILING_TILE_INNER_MIC_SCALE = 0.7;

/**
 * MUI Devices component
 *
 * @description Renders and manages interactive device icons with move functionality.
 */
const MuiDevices: React.FC<MuiDeviceProps> = (props) => {
  const { bbox, scale, zoom, iconAdj } = useSelector(getDrawingBox);
  const { id, type, model, origin: { left, top }, devices, icon, show, active: isActive } = props;
  
  const deviceType = icon;
  const x0 = bbox.x0 + left * scale * (!zoom ? 1 : 0);
  const y0 = bbox.y0 + top * scale * (!zoom ? 1 : 0);
  // Unique per rendered device group (multiple MuiDevices instances mount per area),
  // used to clip the DEBUG coverage circles to the room's bounding box (see below).
  const debugClipId = `${id}-${type}-debug-clip`;

  const Icon = useMemo(() => icons[icon], [icon]);
  const iconSize = 46;

  const layout = has(devices, 'layout') ? (devices as DeviceCollection).layout : (devices as DeviceLayoutItem[]);

  // Get context to check if device is a bar microphone, speaker, or camera (needed before layoutArray)
  const context = useContext(ToolContext);
  const { isBarSpkr = false, isBarCamera = false } = context.constants.equipment?.[type]?.[model] ?? {};
  const isBarMic = type === 'microphones' && micInCategory(model, 'BAR');
  const isCeilingMounted = micInCategory(model, 'CBC') || (isBarSpkr && micInCategory((devices as any)?._micModelKey, 'CBC'));

  // BMA360D ceiling-tile mics: the rounded-square body is drawn at the size of the
  // physical ceiling tile they occupy (physicalDimensions, in metres). The mic glyph
  // inside is kept at the normal icon size via `iconInnerScale`, which the icon applies
  // as an inverse scale so it counteracts the larger outer tile scale.
  const isCeilingTileMic = type === 'microphones' && micInCategory(model, 'CEILING_TILE');
  const ceilingTileFootprint = (context.constants.equipment?.microphones?.[model] as any)?.physicalDimensions?.x;
  const normalIconScale = Math.max(10 / iconSize, scale * iconAdj); //wd = 10 & 50 CRD=180
  const useTileScale = Boolean(isCeilingTileMic && ceilingTileFootprint);
  const iconScale = useTileScale ? (ceilingTileFootprint * scale) / CEILING_TILE_ICON_BODY_UNITS : normalIconScale;
  const iconInnerScale = useTileScale ? CEILING_TILE_INNER_MIC_SCALE : 1;

  const layoutArray: DeviceObject[] = (layout || []).map((dev, index) => {
    // Include rotation if specified (for wall speakers, bar microphones, bar speakers, or bar cameras)
    // For bar devices (mic/speaker/camera), use horAngle for icon rotation to align with table
    // Bar devices (mic/speaker/camera) use horAngle in rotation convention (0 = right).
    // Standalone cameras use horAngle in compass convention (90 = right), so subtract 90 to convert.
    const standaloneCamera = type === 'cameras' && !isBarCamera;
    const cameraIconRotation = standaloneCamera && dev.horAngle !== undefined
      ? dev.horAngle % 360
      : null;
    const rotation = (dev as any).rotation ||
      ((isBarCamera || isBarMic || isBarSpkr) && dev.horAngle !== undefined ? (dev.horAngle + 90) % 360 : null) ||
      cameraIconRotation;
    const baseAdjustedRotation = (isBarMic || isBarSpkr) && !isCeilingMounted ? ((rotation || 0) + 90) % 360 : rotation;
    const adjustedRotation = (!isBarCamera && baseAdjustedRotation !== null && ['videobar', 'videobar2800', 'audiobar', 'ceiling_bar'].includes(icon))
      ? ((baseAdjustedRotation || 0) + 90) % 360
      : baseAdjustedRotation;
    const rotationTransform = adjustedRotation ? ` rotate(${adjustedRotation})` : '';
    return {
      id: `${id}-${type}-${index}`,
      transform: `translate(${x0 + dev.location.x * scale}, ${y0 + dev.location.y * scale})${rotationTransform} scale(${iconScale})`,
      // Standalone cameras use compass convention (0=north), subtract 90 to convert to display angle.
      // Bar cameras use rotation convention (0=east/right) which is already the correct display angle.
      angle: dev.horAngle !== undefined ? dev.horAngle : 0,
    };
  });

  const visible = useSelector((state: RootState) => getConditionResult(state, { ...show, area: id }));
  const active = useSelector((state: RootState) => getConditionResult(state, { ...isActive, area: id }));

  const Arrow = icons['speakerArrow'];
  const shoudShowArrow = devices && devices.length > 0 ? 'horAngle' in (devices as any)[0] : false;

  const showCoverageOverlaps = useSelector((state: RootState) => getCurrentValue2(state, 'showOverlaps'));
  const collisions = useSelector((state: RootState) => getCurrentValue2(state, 'collisions'));

  // Wall speaker visualization
  const isWallSpeaker = type === 'speakers' && (devices as any)?.mountingType === 'wall';
  const linkedSpeakers = useSelector((state: RootState) => getCurrentValue2(state, 'speakers.linkedSpeakers'));

  // Get current speaker, microphone, and camera layouts for bar device updates
  const speakersState = useSelector((state: RootState) => getCurrentValue2(state, 'speakers'));
  const microphonesState = useSelector((state: RootState) => getCurrentValue2(state, 'microphones'));
  const camerasState = useSelector((state: RootState) => getCurrentValue2(state, 'cameras'));

  // Get ceiling height for bar speaker height fallback
  const ceilingHeight = useSelector((state: RootState) => getCurrentValue2(state, 'ceilingHeight'));

  // Get room dimensions for wall constraint
  const roomDimensions = useSelector((state: RootState) => getCurrentValue2(state, 'dimensions'));
  // Table (used only by the DEBUG bar-coverage circle to derive the bar's SPL-decay reach).
  const debugTable = useSelector((state: RootState) => getCurrentValue2(state, 'table'));

  // Get the current action to set whether the icons will receive pointer events or not
  const currentAction = useSelector((state: RootState) => getCurrentValue2(state, 'action'));
  const currentDevice = useSelector((state: RootState) => getCurrentValue2(state, 'currentDevice'));
  const selectedDevice = useSelector((state: RootState) => getCurrentValue2(state, 'selectedDevice'));
  const isIconClickable = active && ['remove', 'move'].includes(currentAction as string);
  const iconThemes = useIconThemes();
  const state = iconThemes[active ? 'button_active' : 'button_disabled-dim'];
  const selectedState = iconThemes['button_selected'];

  // Move device functionality starts here
  const devicesRef = useRef<Map<DeviceObject, SVGGElement>>(null as any);
  const justFinishedDragging = useRef(false);
  const [moving, setMoving] = useState<boolean>(false);
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [currentDragDevice, setCurrentDragDevice] = useState<DeviceObject | null>(null);
  const mouse = useMousePosition(true, true);
  const dispatch = useDispatch();

  // isBarMic, isBarSpkr, isBarCamera, context, and isCeilingTileMic are defined earlier (before layoutArray).
  // isCeilingTileMic also gates Esc-to-cancel during drag and the on-release tile pre-check.
  const isCamera = type === 'cameras';
  // BMA360D variants — used to gate Esc-to-cancel during drag.
  // All ceiling mics that snap to the tile grid (TCM-X, CBC CM/PM, BMA360D). The
  // on-release tile pre-check — "moved to nearest tile" toast and the all-blocked
  // revert-to-original + warning — applies to every one of these, not just BMA360D.
  const isCeilingTileDevice = type === 'microphones' && micInCategory(model, 'CEILING_MOUNTED');
  // Ceiling speakers also snap to the tile grid. Like ceiling tile mics, a drop on a blocked
  // tile is not allowed — the speaker returns to its previous position rather than landing on
  // (or relocating off) the blocked tile.
  const isCeilingTileSpeaker = type === 'speakers' && Boolean(context.constants.equipment?.speakers?.[model]?.isCeilingSpkr);
  // Expansion CEILING mics (TCM-X) and CEILING speakers also snap to the tile grid and follow the
  // same rules (centre in tile, revert on a blocked-tile drop). Table expansion mics (TTM-X) and
  // pendant expansion speakers are excluded — they don't tile-snap.
  const isExpansionCeilingMic = type === 'expansionMicrophones' && micInCategory(model, 'CEILING_MOUNTED');
  const isExpansionCeilingSpeaker = type === 'expansionSpeakers' && Boolean(context.constants.equipment?.speakers?.[model]?.isCeilingSpkr);
  // Display name for user-facing tile messages (e.g. "Parlé TCM-X", "BMA360D", "Desono C-IC6").
  // Expansion render groups carry the base equipment category's model, so resolve their name there.
  const deviceNameCategory = type === 'expansionMicrophones' ? 'microphones' : type === 'expansionSpeakers' ? 'speakers' : type;
  const deviceName = context.constants.equipment?.[deviceNameCategory]?.[model]?.modelName || 'Device';

  // Drag-release tile rules (blocked / occupied / coverage) are evaluated by the shared
  // `evaluateTileDrop(state, …)`; the store gives it the live state without re-selecting each input.
  const store = useStore<RootState>();
  const { showWarning } = useAlert();

  // Snapshot of the device's transform/node at drag start so Esc can revert.
  const preDragRef = useRef<{ transform: string, node: SVGGElement, pointerId: number } | null>(null);
  // Set true by the Esc handler so the subsequent pointerUp short-circuits without dispatching updateDevice.
  const dragCancelledRef = useRef(false);

  /**
   * Cleanup effect to ensure moving state is reset when component unmounts
   */
  useEffect(() => {
    return () => {
      // Clean up on unmount - reset moving state
      if (moving) {
        setMoving(false);
        setCurrentDragDevice(null);
        dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
        dispatch(ReducersIndex.uiReducers.setDeviceIsMoving(false));
      }
    };
  }, [moving, dispatch]);

  /**
   * Esc-to-cancel for BMA360D drags: revert the visual position to where the device sat
   * before mouse-down and reset state without dispatching updateDevice.
   */
  useEffect(() => {
    if (!moving || !isCeilingTileMic) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const snapshot = preDragRef.current;
      if (!snapshot) return;

      dragCancelledRef.current = true;
      try { snapshot.node.setAttribute('transform', snapshot.transform); } catch { /* noop */ }
      try { snapshot.node.releasePointerCapture(snapshot.pointerId); } catch { /* noop */ }

      setMoving(false);
      setCurrentDragDevice(null);
      dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
      dispatch(ReducersIndex.uiReducers.setDeviceIsMoving(false));
      preDragRef.current = null;

      e.stopPropagation();
      e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [moving, isCeilingTileMic, dispatch]);

  // After a drag ends, React may skip updating the SVG transform when the snapped position
  // equals the pre-drag position (same vdom string → no DOM patch). This effect forces the
  // DOM transform to match layoutArray after every drag release so snap is always visible.
  useLayoutEffect(() => {
    if (!justFinishedDragging.current) return;
    justFinishedDragging.current = false;
    const map = getMap();
    layoutArray.forEach(device => {
      const node = map.get(device);
      if (node && device.transform) {
        node.setAttribute('transform', device.transform);
      }
    });
  });

  if (!Icon) return null;

  /**
   * @function getMap
   * @description Initializes and retrieves the reference map for devices.
   * @returns {Map} Map object for storing device references
   */
  const getMap = (): Map<DeviceObject, SVGGElement> => {
    if (!devicesRef.current) {
      devicesRef.current = new Map();
    }
    return devicesRef.current;
  };

  /**
   * @function getTranslateValues
   * @description Extracts translate values from a transform string.
   * @param {string} transformString - SVG transform attribute string
   * @returns {Object|null} Object with x and y translate values, or null if not found
   */
  const getTranslateValues = (transformString: string): { x: number, y: number } | null => {
    const match = transformString.match(/translate\(([^)]+)\)/);
    if (match && match[1]) {
      const values = match[1].split(',').map(Number);
      return { x: values[0], y: values[1] };
    }
    return null;
  };

  /**
   * @function setTranslateValues
   * @description Updates the translate values of an SVG element.
   * @param {SVGElement[]} nodes - The SVG elements to update
   * @param {number} x - New x coordinate
   * @param {number} y - New y coordinate
   */
  const setTranslateValues = (nodes: SVGGElement[], x: number, y: number): void => {
    nodes.forEach(node => {
      const currentTransform = node.getAttribute('transform') || '';
      const scaleMatch = currentTransform.match(/scale\(([^)]+)\)/);
      const scaleValue = scaleMatch ? scaleMatch[1] : '1';
      const newTransform = `translate(${x}, ${y}) scale(${scaleValue})`;
      node.setAttribute('transform', newTransform);
    });
  };

  /**
   * @function limitMove
   * @description Limits the movement of a device within the room boundaries.
   * @param {number} x - Proposed x coordinate
   * @param {number} y - Proposed y coordinate
   * @returns {Object} Object with limited x and y coordinates
   */
  const limitMove = (x = 0, y = 0): { x: number, y: number } => {
    // Limit range of motion to keep device inside room
    const fence = iconSize * iconScale * .5;
    x = clamp(x - offset.x, bbox.x0 + fence, bbox.x1 - fence - 2);
    y = clamp(y - offset.y, bbox.y0 + fence, bbox.y1 - fence - 2);

    // Room diagonal - angle of the diagonal line that goes from the top-left corenr to the center of the room
    const roomDiagonal = Math.atan2(bbox.y0 - bbox.cy, bbox.x0 - bbox.cx) / (Math.PI / 180) + 180;

    if (isBarMic || isBarSpkr || isCamera || isWallSpeaker) {
      // Angle of the coordinates with respect to the center
      const angle = Math.atan2((y - bbox.cy), (x - bbox.cx)) / (Math.PI / 180) + 180;
      switch (true) {
        case angle <= roomDiagonal: // Left Side
          x = bbox.x0 + fence;
          break;
        case angle <= 180 - roomDiagonal: // Upper Side
          y = bbox.y0 + fence;
          break;
        case angle <= 180 + roomDiagonal: // Right Side
          x = bbox.x1 - fence - 2;
          break;
        case angle <= 360 - roomDiagonal: // Lower Side
          y = bbox.y1 - fence - 2;
          break;
        case angle <= 360 + roomDiagonal: // Left Side
          x = bbox.x0 + fence;
          break;
        default:
          x = bbox.x0 + fence;
          break;
      }
    }
    return { x, y };
  };

  /**
   * @function constrainToWall
   * @description Constrains a location to the nearest wall for wall speakers
   * @param {Object} location - The location to constrain {x, y}
   * @param {number} roomLength - The room length (X dimension)
   * @param {number} roomWidth - The room width (Y dimension)
   * @returns {Object} The constrained location {x, y}
   */
  const constrainToWall = (location: { x: number, y: number }, roomLength: number, roomWidth: number): { x: number, y: number } => {
    // Offset from wall to prevent icon from overlapping border (matches initial placement logic)
    const wallOffset = 0.15; // meters

    const distToLeft = location.x;
    const distToRight = roomLength - location.x;
    const distToTop = location.y;
    const distToBottom = roomWidth - location.y;

    const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);

    if (minDist === distToLeft) {
      return { x: wallOffset, y: location.y };
    } else if (minDist === distToRight) {
      return { x: roomLength - wallOffset, y: location.y };
    } else if (minDist === distToTop) {
      return { x: location.x, y: wallOffset };
    } else {
      return { x: location.x, y: roomWidth - wallOffset };
    }
  };

  /**
   * @function getWallRotation
   * @description Determines which wall a speaker is on and returns the appropriate rotation angle
   * @param {Object} location - The speaker location {x, y}
   * @param {number} roomLength - The room length (X dimension)
   * @param {number} roomWidth - The room width (Y dimension)
   * @returns {number} Rotation angle: 0° (left), 90° (top), 180° (right), 270° (bottom)
   */
  const getWallRotation = (
    location: { x: number, y: number },
    roomLength: number,
    roomWidth: number
  ): number => {
    const tolerance = 0.5;
    const distToLeft = location.x;
    const distToRight = roomLength - location.x;
    const distToTop = location.y;
    const distToBottom = roomWidth - location.y;

    const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);

    if (minDist === distToLeft) {
      return 0;   // LEFT wall
    } else if (minDist === distToRight) {
      return 180; // RIGHT wall
    } else if (minDist === distToTop) {
      return 90;  // TOP wall
    } else {
      return 270; // BOTTOM wall
    }
  };

  /**
   * @function getMirroredLocation
   * @description Calculates the mirrored position for a linked speaker
   * Left/Right walls (vertical): Mirror end-to-end on SAME wall
   * Top/Bottom walls (horizontal): Mirror to OPPOSITE wall with aligned x position
   * @param {Object} location - The location to mirror {x, y}
   * @param {Object} dimensions - The room dimensions {length, width}
   * @returns {Object} The mirrored location {x, y}
   */
  const getMirroredLocation = (
    location: { x: number, y: number },
    dimensions: { length: number, width: number }
  ): { x: number, y: number } => {
    // Always keep x position the same and mirror y position
    // This achieves:
    // - Left/Right walls: Speakers on same wall, mirrored end-to-end
    // - Top/Bottom walls: Speakers on opposite walls, aligned at same x position
    return { x: location.x, y: dimensions.width - location.y };
  };

  /**
   * @function calculateAimingAngles
   * @description Calculates horizontal and vertical aiming angles for wall speakers
   * @param {Object} speakerLocation - The speaker location {x, y}
   * @param {Object} targetPoint - The target point {x, y}
   * @param {number} speakerHeight - The height of the speaker in meters
   * @returns {Object} An object with horAngle and verAngle properties
   */
  const calculateAimingAngles = (
    speakerLocation: { x: number, y: number },
    targetPoint: { x: number, y: number },
    speakerHeight: number
  ): { horAngle: number, verAngle: number } => {
    const dx = targetPoint.x - speakerLocation.x;
    const dy = targetPoint.y - speakerLocation.y;

    // Horizontal angle (azimuth) in degrees
    let horAngle = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (horAngle < 0) horAngle += 360;

    // Horizontal distance in meters
    const horizontalDistance = Math.sqrt(dx * dx + dy * dy);

    // Vertical angle (elevation) in degrees
    // Listener height from constants (default 1.2m for seated position)
    const listenerHeight = context.constants.area?.speakerAiming?.listenerHeight || 1.2;
    const heightDiff = speakerHeight - listenerHeight;
    let verAngle = -(Math.atan2(heightDiff, horizontalDistance) * 180) / Math.PI;

    // Constrain vertical angle
    const minVerticalAngle = context.constants.area?.speakerAiming?.minVerticalAngle || -45;
    const maxVerticalAngle = context.constants.area?.speakerAiming?.maxVerticalAngle || 45;
    verAngle = Math.max(minVerticalAngle, Math.min(maxVerticalAngle, verAngle));

    return {
      horAngle: Math.round(horAngle),
      verAngle: Math.round(verAngle * 10) / 10
    };
  };

  /**
   * @function onPointerDown
   * @description Handles the pointer down event for device movement.
   * @param {React.PointerEvent} e - The pointer event
   * @param {DeviceObject} device - The device object being moved
   */
  const onPointerDown = (e: React.PointerEvent, device: DeviceObject): void => {
    // Guard: only allow movement if action is 'move' AND device type matches current device menu.
    // Special case: `expansionMicrophones` and `expansionSpeakers` are separate render groups
    // layered above their primary slot (z-order in MuiMicrophones / MuiSpeakers puts the
    // expansion render group above the primary one), so the user actually clicks the
    // expansion group. Treat them as part of microphones / speakers for move purposes,
    // otherwise expansion devices would be non-draggable.
    if (currentAction !== 'move') return;
    const typeMatches = type === currentDevice
      || (type === 'expansionMicrophones' && currentDevice === 'microphones')
      || (type === 'expansionSpeakers' && currentDevice === 'speakers');
    if (!typeMatches) return;
    // Get the device reference
    const map = getMap();
    const node = map.get(device);
    if (!node) return;

    // Set pointer event to device
    node.setPointerCapture(e.pointerId);
    // Calculate mouse to device offset
    const { left, right, top, bottom } = node.getBoundingClientRect();
    setOffset({ x: e.clientX - (right + left) / 2, y: e.clientY - (top + bottom) / 2 });
    // Get the device ready to move
    setMoving(true);
    setCurrentDragDevice(device);
    // Stash the pre-drag transform so Esc can revert without a Redux round-trip.
    preDragRef.current = { transform: device.transform, node, pointerId: e.pointerId };
    dragCancelledRef.current = false;
    dispatch(ReducersIndex.uiReducers.setSelectedDevice(device.id));
    dispatch(ReducersIndex.uiReducers.setDeviceIsMoving(true));
    e.stopPropagation();
  };

  /**
   * @function onPointerMove
   * @description Handles the pointer move event for device movement.
   * @param {React.PointerEvent} e - The pointer event
   * @param {DeviceObject} device - The device object being moved
   */
  const onPointerMove = (e: React.PointerEvent, device: DeviceObject): void => {
    if (!moving) return;
    // Get the device reference
    const map = getMap();
    const node = map.get(device);
    if (!node) return;
    
    const nodes = [node];
    // Get current mouse position
    if (!mouse || mouse.x === undefined || mouse.y === undefined) return;
    let { x, y } = mouse;
    // Apply move limits
    ({ x, y } = limitMove(x, y));

    // For wall speakers, constrain to wall during drag for smooth visual feedback
    // Store the constrained room location for use in linked speaker mirroring
    let constrainedRoomLocation: { x: number, y: number } | null = null;
    if (isWallSpeaker && roomDimensions) {
      constrainedRoomLocation = {
        x: (x - x0) / scale,
        y: (y - y0) / scale
      };
      constrainedRoomLocation = constrainToWall(constrainedRoomLocation, roomDimensions.length, roomDimensions.width);
      // Convert back to screen coordinates for visual update
      x = x0 + constrainedRoomLocation.x * scale;
      y = y0 + constrainedRoomLocation.y * scale;
    }

    // if device is a bar, move the counter element
    if (isBarMic || isBarSpkr) {
      const mirrorId = nodes[0].id.replace(type, isBarMic ? 'speakers' : 'microphones');
      const mirrorElement = document.getElementById(mirrorId);
      // Safety cast only if the element is an SVG element
      const mirrorNode = mirrorElement instanceof SVGElement ? (mirrorElement as unknown as SVGGElement) : null;
      if (mirrorNode) {
        nodes.push(mirrorNode);
      }
    }

    // For linked wall speakers, mirror the other speaker's position during drag
    if (isWallSpeaker && linkedSpeakers && constrainedRoomLocation && roomDimensions) {
      const mirroredRoomLocation = getMirroredLocation(constrainedRoomLocation, roomDimensions);
      const mirroredScreenX = x0 + mirroredRoomLocation.x * scale;
      const mirroredScreenY = y0 + mirroredRoomLocation.y * scale;
      const speakerIndex = parseInt(device.id.split('-').pop() || '0');
      const otherIndex = speakerIndex === 0 ? 1 : 0;
      const otherSpeakerId = device.id.replace(/-\d+$/, `-${otherIndex}`);
      const otherSpeakerElement = document.getElementById(otherSpeakerId);
      const otherSpeakerNode = otherSpeakerElement instanceof SVGGElement ? otherSpeakerElement : null;
      if (otherSpeakerNode) {
        setTranslateValues([otherSpeakerNode], mirroredScreenX, mirroredScreenY);
      }
    }

    // Move the primary device (and any nodes in the array like bar mics)
    setTranslateValues(nodes, x, y);
  };

  /**
   * @function onPointerCancel
   * @description Handles pointer cancel event (e.g. when dragging off screen).
   * @param {React.PointerEvent} e - The pointer event
   * @param {DeviceObject} device - The device object being moved
   */
  const onPointerCancel = (e: React.PointerEvent, device: DeviceObject): void => {
    // Treat pointer cancel the same as pointer up to ensure clean state
    onPointerUp(e, device);
  };

  /**
   * @function onPointerUp
   * @description Handles the pointer up event, finalizing device movement.
   * Uses try-finally to guarantee state cleanup regardless of exit path.
   * @param {React.PointerEvent} e - The pointer event
   * @param {DeviceObject} device - The device object being moved
   */
  const onPointerUp = (e: React.PointerEvent, device: DeviceObject): void => {
    // CLEANUP LOCAL STATE FIRST - before any early exits
    // Capture the current moving state to determine if we need to process this event
    const wasMoving = moving;

    console.log('🔷 [PLOT_DEBUG] onPointerUp called:', {
      deviceId: device.id,
      type,
      wasMoving,
      currentAction,
      timestamp: new Date().toISOString()
    });

    setMoving(false);
    setCurrentDragDevice(null);

    // If Esc cancelled this drag, the keydown handler already reverted the visual position
    // and cleaned up Redux state. Bail before the finalize-move path so we don't dispatch updateDevice.
    if (dragCancelledRef.current) {
      dragCancelledRef.current = false;
      preDragRef.current = null;
      return;
    }

    try {
      // Guard checks - use wasMoving instead of moving since we already reset it
      if (currentAction !== 'move') {
        console.log('⚠️ [PLOT_DEBUG] Early exit: currentAction is not "move":', currentAction);
        return;
      }
      if (!wasMoving) {
        console.log('⚠️ [PLOT_DEBUG] Early exit: wasMoving is false');
        return;
      }

      // Get the device reference
      const map = getMap();
      const node = map.get(device);
      if (!node) {
        console.log('⚠️ [PLOT_DEBUG] Early exit: node not found in map');
        return;
      }

      // Get the index and position of the moved device
      const transform = node.getAttribute('transform') || '';
      const translateValues = getTranslateValues(transform);
      if (!translateValues) {
        console.log('⚠️ [PLOT_DEBUG] Early exit: translateValues not found, transform:', transform);
        return;
      }

    const { x, y } = translateValues;
    const index = parseInt(node.id.split('-').pop() || '0');
    let location = {
      x: (x - x0) / scale,
      y: (y - y0) / scale
    };

    // Ceiling-tile mic drag release: decide whether the drop lands on a tile, snaps far, or has
    // no available tile at all. Applies to every tile-snapping mic (TCM-X, CBC CM/PM, BMA360D),
    // not just BMA360D. The middleware still owns final snap; this pre-check only drives toast UX
    // and the "all tiles blocked → revert to original position" path described in PIG-438.
    //
    // Note: the nearest-tile choice here is plain-Euclidean nearest unblocked. The middleware
    // applies additional rules (whole-tile centring vs. front-half mask preference) and may land
    // the mic on a different tile than the one this helper identifies. For the toast decision
    // that's fine — the "snapped to nearest available" message still describes the user-perceptible
    // behavior — but exact location parity should not be assumed.
    if (isCeilingTileDevice || isCeilingTileSpeaker || isExpansionCeilingMic || isExpansionCeilingSpeaker) {
      // Tile-snapping devices (ceiling mics TCM-X / CBC / BMA360D, ceiling speakers, and expansion
      // ceiling mics / speakers) are never relocated to a different tile on drop. The drop is
      // validated by the SHARED tile rules (`evaluateTileDrop`) — the same function the add paths
      // use — which checks blocked / occupied and excludes this device's own entry (by index) from
      // the occupancy set. A free tile under the drop is accepted (the middleware applies whole-tile
      // centring or BMA side-snap).
      const landing = evaluateTileDrop(store.getState(), location, { deviceType: type, model, index });
      if (
        landing.outcome === 'dropped_on_blocked' ||
        landing.outcome === 'all_blocked' ||
        landing.outcome === 'dropped_on_occupied'
      ) {
        // Return the device to where it sat before the drag and warn the user, rather than
        // leaving it on a blocked / occupied tile at the drop point, or relocating it elsewhere.
        if (preDragRef.current) {
          try { preDragRef.current.node.setAttribute('transform', preDragRef.current.transform); } catch { /* noop */ }
        }
        preDragRef.current = null;
        const warningMsg =
          landing.outcome === 'dropped_on_occupied'
            ? `That tile already has a device — ${deviceName} returned to its previous position.`
            : `That ceiling tile is blocked — ${deviceName} returned to its previous position.`;
        showWarning(warningMsg);
        return;
      }
    }

    // Apply wall constraint for wall speakers
    if (isWallSpeaker && roomDimensions) {
      location = constrainToWall(location, roomDimensions.length, roomDimensions.width);
    }

    // Calculate rotation for the dragged speaker
    let otherIndex: number | null = null;
    let draggedRotation: number | undefined = undefined;
    let mirroredLocation: { x: number, y: number } | undefined = undefined;

    if (isWallSpeaker && linkedSpeakers && layout && layout.length === 2 && roomDimensions) {
      // Get the speaker index from device.id
      const deviceId = device.id;
      const speakerIndex = parseInt(deviceId.split('-').pop() || '0');
      otherIndex = speakerIndex === 0 ? 1 : 0;

      // Calculate rotation based on which wall the dragged speaker is on
      draggedRotation = getWallRotation(location, roomDimensions.length, roomDimensions.width);

      // Calculate mirrored location for linked speaker
      mirroredLocation = getMirroredLocation(location, roomDimensions);
    } else if (isWallSpeaker && roomDimensions) {
      // For single wall speaker or unlinked mode, still calculate rotation
      draggedRotation = getWallRotation(location, roomDimensions.length, roomDimensions.width);
    }

    // Calculate aiming angles for wall speakers
    let draggedAngles: { horAngle: number, verAngle: number } | undefined = undefined;
    let otherSpeakerAngles: { horAngle: number, verAngle: number } | undefined = undefined;

    if (isWallSpeaker && (devices as DeviceCollection)?.targetPoint && (devices as DeviceCollection)?.height) {
      const targetPoint = (devices as DeviceCollection).targetPoint;
      const speakerHeight = (devices as DeviceCollection).height;

      // Calculate angles for dragged speaker
      draggedAngles = calculateAimingAngles(location, targetPoint, speakerHeight);

      // For linked speakers, recalculate angles for the other speaker using its MIRRORED location
      if (otherIndex !== null && mirroredLocation) {
        otherSpeakerAngles = calculateAimingAngles(mirroredLocation, targetPoint, speakerHeight);
      }
    }

    // Get the custom parameters and clean the object keys
    let customParameters = context.constants.customSettings[type];
    customParameters = mapKeys(customParameters, (_, key) => key.split('.').pop());

    // Explicitly preserve coverage from current devices state
    // This ensures user's coverage preference is maintained when moving devices
    if ((devices as DeviceCollection)?.coverage) {
      customParameters.coverage = (devices as DeviceCollection).coverage;
    }

    // Create the updated layout
    // For object devices, exclude plotData to prevent stale data
    // For array devices, there's no plotData to exclude
    // Carry every existing device-collection property forward (model, qty, coverage, …) and
    // only strip `plotData` so the middleware recomputes it from the new layout. Notable
    // side-effect (PIG-439): this is how the user-adjusted `microphones.height` survives a
    // drag — it lives on the collection object and is preserved by this spread. If a future
    // change adds more keys to the strip list, height preservation is at risk; see
    // `calculateDevicesMiddleware.bma360d.test.ts` "PIG-439" block for the pin.
    const baseDeviceProps = Array.isArray(devices)
      ? {}
      : Object.keys(devices || {}).reduce((acc, key) => {
          if (key !== 'plotData') {
            acc[key] = (devices as any)[key];
          }
          return acc;
        }, {} as any);

    const speakerMeta: SpeakerPayloadMeta = { _forceSpeakerRecalc: true };
    const updatedLayout: { [key: string]: any } = {
      [type]: {
        ...baseDeviceProps,
        ...customParameters,
        ...speakerMeta,  // Force immediate HearSpace recalculation, bypass debounce
        layout: Array.isArray(devices)
          ? (devices || []).map((item, i) => {
              if (i === index) {
                // Update dragged speaker with new location, rotation, and aiming angles
                const update: any = { ...item, location };
                if (draggedRotation !== undefined) {
                  update.rotation = draggedRotation;
                }
                if (draggedAngles) {
                  update.horAngle = draggedAngles.horAngle;
                  update.verAngle = draggedAngles.verAngle;
                }
                return update;
              } else if (otherIndex !== null && i === otherIndex) {
                // For linked speakers, update the other speaker's position, rotation, and angles
                const update: any = { ...item };
                if (mirroredLocation) {
                  update.location = mirroredLocation;
                  // Calculate rotation for mirrored speaker
                  if (roomDimensions) {
                    update.rotation = getWallRotation(mirroredLocation, roomDimensions.length, roomDimensions.width);
                  }
                }
                if (otherSpeakerAngles) {
                  update.horAngle = otherSpeakerAngles.horAngle;
                  update.verAngle = otherSpeakerAngles.verAngle;
                }
                return update;
              }
              return item;
            })
          : ((devices as DeviceCollection)?.layout || []).map((item, i) => {
              if (i === index) {
                // Update dragged speaker with new location, rotation, and aiming angles
                const update: any = { ...item, location };
                if (draggedRotation !== undefined) {
                  update.rotation = draggedRotation;
                }
                if (draggedAngles) {
                  update.horAngle = draggedAngles.horAngle;
                  update.verAngle = draggedAngles.verAngle;
                }
                // For cameras, remove horAngle so the reducer recalculates it from the new position
                if (type === 'cameras') {
                  delete update.horAngle;
                  delete update.aimPoint;
                }
                return update;
              } else if (otherIndex !== null && i === otherIndex) {
                // For linked speakers, update the other speaker's position, rotation, and angles
                const update: any = { ...item };
                if (mirroredLocation) {
                  update.location = mirroredLocation;
                  // Calculate rotation for mirrored speaker
                  if (roomDimensions) {
                    update.rotation = getWallRotation(mirroredLocation, roomDimensions.length, roomDimensions.width);
                  }
                }
                if (otherSpeakerAngles) {
                  update.horAngle = otherSpeakerAngles.horAngle;
                  update.verAngle = otherSpeakerAngles.verAngle;
                }
                return update;
              }
              return item;
            })
      }
    };

    /**
     * Updates the layout for single unit devices (audio bars or video bars).
     * This function moves all components of the device to a new position.
     */
    const deviceLayout = updatedLayout[type].layout;
    const newPosition = deviceLayout[0]?.location;
    if (newPosition && (isBarMic || isBarSpkr || isBarCamera || deviceType?.startsWith('videobar'))) {
      // When moving a bar mic/speaker, sync speakers + microphones (and cameras for VBC)
      // When moving a bar camera, sync microphones + speakers so the bar follows the camera.
      // Do NOT add 'cameras' when isBarCamera — the camera position is already set from the drag itself.
      const componentsToUpdate = ['speakers', 'microphones'];
      if (deviceType?.startsWith('videobar') && !isBarCamera) {
        componentsToUpdate.push('cameras');
      }
      componentsToUpdate.forEach(component => {
        // Use the correct existing state for each component type
        const componentState = component === 'speakers' ? speakersState : component === 'cameras' ? camerasState : microphonesState;
        const existingLayout = componentState?.layout?.[0] || {};

        // Calculate fallback height for bar devices
        // ALWAYS use ceiling height for bar speakers (not table height from location.z)
        const barHeight = (ceilingHeight as any)?.min || 2.8956;

        // Preserve all properties and fill in missing ones with correct defaults
        updatedLayout[component] = {
          layout: [{
            ...existingLayout,
            location: {
              ...newPosition,
              z: barHeight  // Ensure z coordinate is set to proper height
            },
            // Fill in missing properties for bar devices
            horAngle: existingLayout.horAngle !== undefined ? existingLayout.horAngle : 0,
            verAngle: 0,  // Bar speakers always have fixed vertical angle of 0
            height: barHeight
          }]
        };
      });
    }

    // Mark bar devices as customized to prevent automatic recalculation.
    // Only include cameras for video bars (VBC) with an integrated camera — audio-only bars
    // (ABC 2500a, deviceType='audiobar') have no camera so cameras must not be marked customized.
    if (isBarMic || isBarSpkr || isBarCamera || deviceType?.startsWith('videobar')) {
      const hasIntegratedCamera = isBarCamera || deviceType?.startsWith('videobar');
      const customizedPayload: Record<string, boolean> = { microphones: true, speakers: true };
      if (hasIntegratedCamera) customizedPayload.cameras = true;
      dispatch((ReducersIndex.container as any).setCustomized(customizedPayload));
    }

    // Debug logging for all device updates
    console.log('✅ [PLOT_DEBUG] Dispatching updateDevice:', {
      type,
      deviceId: device.id,
      index,
      newLocation: location,
      isWallSpeaker,
      isBarMic,
      isBarSpkr,
      timestamp: new Date().toISOString(),
      layoutLength: updatedLayout[type]?.layout?.length,
      updatedLayout: JSON.stringify(updatedLayout, null, 2)
    });

    // Debug logging for wall speaker updates
    if (isWallSpeaker) {
      console.log('🔷 [MuiDevices] onPointerUp - Wall speaker details:', {
        type,
        draggedSpeaker: {
          index,
          location,
          rotation: draggedRotation,
          horAngle: draggedAngles?.horAngle,
          verAngle: draggedAngles?.verAngle
        },
        otherSpeaker: otherIndex !== null && layout ? {
          index: otherIndex,
          location: layout[otherIndex]?.location,
          rotation: layout[otherIndex]?.rotation,
          updatedAngles: {
            horAngle: otherSpeakerAngles?.horAngle,
            verAngle: otherSpeakerAngles?.verAngle
          }
        } : null,
        linkedSpeakers,
        updatedLayout: updatedLayout[type]
      });
    }

      // Use type assertion for the container actions since updateDevice may not be fully typed in the reducer
      dispatch((ReducersIndex.container as any).updateDevice(updatedLayout));

    } finally {
      // GUARANTEED cleanup - this always runs, even if we early-exit or throw an error
      console.log('🔷 [PLOT_DEBUG] onPointerUp finally block:', {
        wasMoving,
        willCleanupRedux: wasMoving,
        timestamp: new Date().toISOString()
      });

      if (wasMoving) {
        justFinishedDragging.current = true;
        // Clean up Redux state
        dispatch(ReducersIndex.uiReducers.setSelectedDevice(null));
        dispatch(ReducersIndex.uiReducers.setDeviceIsMoving(false));
        console.log('✅ [PLOT_DEBUG] Redux state cleanup dispatched');
      }

      // Release pointer capture safely
      try {
        const map = getMap();
        const node = map.get(device);
        if (node) {
          node.releasePointerCapture(e.pointerId);
          console.log('✅ [PLOT_DEBUG] Pointer capture released');
        }
      } catch (err) {
        // Ignore errors if pointer capture was already released
        console.warn('⚠️ [PLOT_DEBUG] Failed to release pointer capture:', err);
      }
    }
  };

  return (
    visible && (
      <DevicesGroup id={`${id}-${type}`} isIconClickable={isIconClickable}>
        {layoutArray && layoutArray.map((device, i) => (
          <g
            key={device.id}
            className={type}
            id={device.id}
            ref={(node) => {
              const map = getMap();
              if (node) {
                map.set(device, node);
              } else {
                map.delete(device);
              }
            }}
            transform={device.transform}
            onPointerDown={(e) => onPointerDown(e, device)}
            onPointerMove={(e) => onPointerMove(e, device)}
            onPointerUp={(e) => onPointerUp(e, device)}
            onPointerCancel={(e) => onPointerCancel(e, device)}
          >
            <Icon state={device.id === selectedDevice ? selectedState : state} innerScale={iconInnerScale} />
            {shoudShowArrow && <Arrow state={state} angle={device.angle} />}
          </g>
        ))}
        {showCoverageOverlaps && has(collisions, type) && (collisions[type] as Array<{ x: number, y: number }>).map((collision, i) => (
          <DeviceCollision
            key={i}
            cx={collision.x * scale + x0}
            cy={collision.y * scale + y0}
            r={0.86 * scale}
          />
        ))}
        {/* DEBUG: clip the debug-coverage circles below to the room's bounding box, not the
            canvas viewport, so they don't draw over/beyond the room walls. */}
        <defs>
          <clipPath id={debugClipId}>
            <rect x={bbox.x0} y={bbox.y0} width={bbox.width} height={bbox.height} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${debugClipId})`}>
          {/* DEBUG: dotted coverage circle at each speaker using its own −6 dB radius
              = (speakerHeight − talkerHeight) × tan(coverageAngle). Toggle with the flag below. */}
          {context.constants.debugSpeakerCoverageCircles && (type === 'speakers' || type === 'expansionSpeakers') && (() => {
            const covAngle = context.constants.equipment?.speakers?.[model]?.coverageAngle;
            if (!covAngle || covAngle <= 0) return null;
            const talkerHeight = context.constants.area?.talkerHeight ?? 1.2009;
            return (layout || []).map((dev: any, i: number) => {
              const height = dev.location?.z ?? (ceilingHeight as any)?.min ?? 2.8956;
              const radiusM = (height - talkerHeight) * Math.tan((covAngle * Math.PI) / 180);
              if (!(radiusM > 0)) return null;
              return (
                <SpeakerCoverageDebug
                  key={`cov-dbg-${i}`}
                  cx={x0 + dev.location.x * scale}
                  cy={y0 + dev.location.y * scale}
                  r={radiusM * scale}
                />
              );
            });
          })()}
          {/* DEBUG: bar speaker reach. The bar fires from the wall into the room, so its real
              coverage is a forward lobe rather than the full circle drawn here — see comment. */}
          {context.constants.debugSpeakerCoverageCircles && type === 'speakers' && isBarSpkr && (() => {
            const c: any = context.constants;
            const dims: any = roomDimensions;
            const tbl: any = debugTable;
            const hasTable = Boolean(tbl?.shape);
            const refDistance = hasTable
              ? Math.max(0.01, ((dims?.length ?? 0) - (tbl?.size?.length ?? 0)) / 2 + (tbl?.transform?.x ?? 0))
              : (c.barSpeakerNoTableRefDistance ?? 1.524);
            const barReach = refDistance * Math.pow(10, (c.barSpeakerDecayThreshold ?? 6) / 20);
            if (!(barReach > 0)) return null;
            return (layout || []).map((dev: any, i: number) => (
              <BarCoverageDebug
                key={`bar-cov-dbg-${i}`}
                cx={x0 + dev.location.x * scale}
                cy={y0 + dev.location.y * scale}
                r={barReach * scale}
              />
            ));
          })()}
        </g>
      </DevicesGroup>
    )
  );
};

// Styled components using MUI styled API
const DevicesGroup = styled('g')<DeviceGroupProps>(({ isIconClickable }) => ({
  pointerEvents: isIconClickable ? 'auto' : 'none',
  // Ensure devices appear above heatmaps and other background elements
  zIndex: 10,
}));

const DeviceCollision = styled('circle')<DeviceCollisionProps>({
  fill: 'none',
  stroke: '#007bff',
  strokeWidth: 1,
  strokeDasharray: '5, 5',
  pointerEvents: 'none',
});

// DEBUG ONLY: dotted coverage circles are gated by the `debugSpeakerCoverageCircles` flag in the
// tool constants (see conferenceroomDesigner/constants.js). Not a UI control — flip that flag to
// visualize coverage while debugging. Read here from the tool context's constants.

const SpeakerCoverageDebug = styled('circle')({
  fill: 'none',
  stroke: '#ff6600',
  strokeWidth: 1,
  strokeDasharray: '4, 4',
  pointerEvents: 'none',
});

// DEBUG ONLY: bar speaker SPL-decay reach (barReach). Distinct colour from the ceiling/pendant
// coverage circles. NOTE: the bar radiates forward from the wall, so its true coverage is a
// half-disc / lobe — this full circle overshoots on the wall side; clip to a semicircle if needed.
const BarCoverageDebug = styled('circle')({
  fill: 'none',
  stroke: '#0aa',
  strokeWidth: 1,
  strokeDasharray: '4, 4',
  pointerEvents: 'none',
});

/**
 * Component for rendering horn speakers.
 */
const MuiHorns: React.FC<MuiHornsProps> = (props) => {
  const area = props.children;
  return (
    <MuiDevices 
      {...props} 
      id={area.id} 
      type={'speakers'} 
      model={area.speakers?.[0]?.model || ''} 
      icon={'speaker'} 
      origin={area.location} 
      devices={area.speakers ?? []} 
    />
  );
};

/**
 * Component for rendering speakers.
 */
const MuiSpeakers: React.FC<MuiSpeakersProps> = (props) => {
  const area = props.children;
  const { speakers, expansionSpeakers } = area as any;
  const spkrType = speakers?.model || 'unknown';
  const context = useContext(ToolContext);
  const icon = context?.constants?.equipment?.speakers?.[spkrType]?.icon?.(area) || 'speaker';

  // CBC bar speakers share model='barspkr' — pass mic model key so MuiDevices can detect ceiling-mount via micInCategory
  const micModelKey = (area.microphones as any)?.model;
  const devicesWithContext = micModelKey
    ? { ...speakers, _micModelKey: micModelKey }
    : speakers;

  // Expansion speakers render as a second MuiDevices group, mirroring how MuiMicrophones
  // surfaces expansion mics. Icons resolve from the same equipment config the primary
  // speakers use (`equipment.speakers[model].icon(area)`), so ceiling vs pendant render
  // with their correct icon shape. Skipped when no expansion speakers are configured.
  const hasExpansionSpeakers = Boolean(
    expansionSpeakers?.model &&
    Array.isArray(expansionSpeakers?.layout) &&
    expansionSpeakers.layout.length > 0
  );
  const expansionSpkrModel = hasExpansionSpeakers ? expansionSpeakers.model : null;
  const expansionIcon = expansionSpkrModel
    ? (context?.constants?.equipment?.speakers?.[expansionSpkrModel]?.icon?.(area) || 'speaker')
    : null;

  return (
    <Fragment>
      <MuiDevices
        {...props}
        id={area.id}
        type={'speakers'}
        model={spkrType}
        icon={icon}
        origin={area.location}
        devices={devicesWithContext}
      />
      {hasExpansionSpeakers && expansionIcon && expansionSpkrModel && (
        <MuiDevices
          {...props}
          id={`${area.id}-expansion-spkr`}
          type={'expansionSpeakers'}
          model={expansionSpkrModel}
          icon={expansionIcon}
          origin={area.location}
          devices={expansionSpeakers}
        />
      )}
    </Fragment>
  );
};

/**
 * Component for rendering microphones.
 */
// Maps product config keys to the icon key used in Devices.js
const MIC_ICON_MAP: Record<string, string> = {
  'tcm_x':       'ceiling_mic',
  'dcm_x':       'ceiling_mic', // ceiling expansion mic — same icon as TCM-X
  'tcm_1':       'pendant_mic',
  'ttm_x':       'tabletop_mic',
  'vbc_2500a':   'videobar',
  'vbc_2800':    'videobar2800',
  'abc_2500a':   'audiobar',
  'cbc_2500_cm': 'ceiling_bar',
  'cbc_2500_pm': 'ceiling_bar',
  'bma360d_24':  'ceiling_tile_mic',
  'bma360d_600': 'ceiling_tile_mic',
};

const MuiMicrophones: React.FC<MuiMicrophonesProps> = (props) => {
  const area = props.children;
  const { microphones, expansionMicrophones } = area as any;
  const micType = microphones?.model || 'unknown';
  const showDevices = useSelector((state: RootState) => getCurrentValue2(state, 'showDevices'));
  const iconType = MIC_ICON_MAP[micType] ?? 'ceiling_mic';

  // Bar configurations: ceiling expansion mics (model: 'dcm_x') are mirrored into
  // microphones.layout via getDeviceLayouts' allMicCoords. They are NOT rendered from
  // that slot — they're rendered via the dedicated `expansionMicrophones` group below
  // (render #3) so move dispatches land on `area.expansionMicrophones.layout` and
  // can't corrupt the bar's index 0 entry via the reducer's positional merge.
  //
  // Here we just filter the primary render down to non-dcm_x entries (the bar alone, in
  // bar configurations; the full layout otherwise).
  // Ceiling expansion mics are 'dcm_x'; legacy saved designs may still carry 'tcm_x' here
  // (created before the dcm_x switch), so accept both. Within an `expansionMicType === 'ceiling'`
  // config the primary mic is always a bar, so neither key collides with the primary entry.
  //
  // Gated on the PRIMARY being a bar rather than on `expansionMicType === 'ceiling'`. The two
  // agree in a settled ceiling-expansion design (that config's primary is always a bar), but not
  // in the frame right after the user switches Ceiling → Table: the reducer flips
  // `expansionMicType` to 'table' and empties `expansionMicrophones.layout` immediately, while
  // `microphones.layout` keeps the mirrored dcm_x entries until the deferred placement pass
  // overwrites it a couple of animation frames later. Keying on the type therefore stopped
  // filtering exactly when the mirrors were still present, and the primary group rendered each
  // one with the BAR icon (`iconType` is derived from the primary model) — a flash of bar icons
  // across the table before the real tabletop mics appeared. A bar primary never owns a
  // dcm_x/tcm_x entry of its own, so dropping them whenever the primary is a bar is safe in both
  // states; a genuine standalone tcm_x design (non-bar primary) is untouched.
  const isCeilingExpansionModel = (m: string) => m === 'dcm_x' || m === 'tcm_x';
  const layoutArray: any[] = microphones?.layout ?? [];
  const hasCeilingExpInLayout = micInCategory(micType, 'BAR')
    && layoutArray.some((m: any) => isCeilingExpansionModel(m.model));
  const primaryDevices = hasCeilingExpInLayout
    ? { ...microphones, layout: layoutArray.filter((m: any) => !isCeilingExpansionModel(m.model)) }
    : microphones;

  // Single render path for all expansion mics — both ceiling (dcm_x) and table (ttm_x).
  // Moves dispatch into area.expansionMicrophones.layout via the standard updateDevice
  // reducer, keeping them isolated from area.microphones.layout (where the bar lives).
  const hasExpansionMics = Boolean(
    expansionMicrophones?.model &&
    Array.isArray(expansionMicrophones?.layout) &&
    expansionMicrophones.layout.length > 0
  );
  const expansionIconType = hasExpansionMics
    ? (isCeilingExpansionModel(expansionMicrophones.model) ? 'expansion_mic' : 'expansion_table_mic')
    : null;

  return (
    <Fragment>
      {showDevices && (
        <MuiDevices
          {...props}
          id={area.id}
          type={'microphones'}
          model={micType}
          icon={iconType}
          origin={area.location}
          devices={primaryDevices}
        />
      )}
      {showDevices && hasExpansionMics && expansionIconType && (
        <MuiDevices
          {...props}
          id={`${area.id}-expansion`}
          type={'expansionMicrophones'}
          model={expansionMicrophones.model}
          icon={expansionIconType}
          origin={area.location}
          devices={expansionMicrophones}
        />
      )}
    </Fragment>
  );
};

/**
 * Component for rendering camera.
 */
const MuiCameras: React.FC<MuiCamerasProps> = (props) => {
  const area = props.children;
  const { cameras } = area;
  const context = useContext(ToolContext);
  if (!cameras.hasCamera || cameras.model === 'none') return null;
  
  // Check if the camera model exists in the equipment constants
  const cameraEquipment = context?.constants?.equipment?.cameras?.[cameras.model];
  if (!cameraEquipment || !cameraEquipment.icon) {
    return null;
  }
  
  const icon = cameraEquipment.icon(area);
  return (
    <MuiDevices 
      {...props} 
      id={area.id} 
      type={'cameras'} 
      model={cameras.model} 
      icon={icon} 
      origin={area.location} 
      devices={cameras} 
    />
  );
};

export { MuiSpeakers, MuiMicrophones, MuiHorns, MuiCameras };
